/**
 * Spenders online on one Account (the CRM's /v1/chats/online-spenders, fed by
 * Keeper's online check): one load and one 5-minute refresh per Account, shared
 * by everything showing it (the side column's block, the phone list's bar).
 */
import { useCallback, useSyncExternalStore } from 'react'
import { crmFetch, dataOf } from '../chat/api'

export interface Spender {
  fan_id: string
  name?: string
  username?: string
  is_online?: boolean
  total_spent?: number
  last_online_at?: string
  source_ppv?: boolean
  source_tip?: boolean
  source_subscription?: boolean
  ppv_spent?: number
  tips_spent?: number
}

export interface SpendersPayload {
  tracking_enabled?: boolean
  socket_held_here?: boolean
  spenders?: Spender[]
}

export type SpendersState = SpendersPayload | 'loading' | 'error'

const REFRESH_MS = 5 * 60_000

interface Snapshot {
  state: SpendersState
  refreshing: boolean
}

interface Feed {
  snap: Snapshot
  listeners: Set<() => void>
  timer: number | null
}

const feeds = new Map<string, Feed>()

function feedOf(accountId: string): Feed {
  let f = feeds.get(accountId)
  if (!f) feeds.set(accountId, (f = { snap: { state: 'loading', refreshing: false }, listeners: new Set(), timer: null }))
  return f
}

/** A new snapshot (never mutated in place, so subscribers re-render). */
function set(f: Feed, change: Partial<Snapshot>) {
  f.snap = { ...f.snap, ...change }
  f.listeners.forEach((fn) => fn())
}

function load(accountId: string): Promise<void> {
  const f = feedOf(accountId)
  return crmFetch(`/v1/chats/online-spenders?account_id=${encodeURIComponent(accountId)}`, { accountId, local: true, method: 'GET' })
    .then((body) => set(f, { state: (dataOf(body) || {}) as SpendersPayload }))
    .catch(() => set(f, { state: 'error' }))
}

function subscribe(accountId: string, fn: () => void): () => void {
  const f = feedOf(accountId)
  f.listeners.add(fn)
  if (f.listeners.size === 1) {
    void load(accountId)
    f.timer = window.setInterval(() => {
      if (!document.hidden) void load(accountId)
    }, REFRESH_MS)
  }
  return () => {
    f.listeners.delete(fn)
    if (!f.listeners.size && f.timer != null) {
      window.clearInterval(f.timer)
      f.timer = null
    }
  }
}

/** Asks Keeper to check again, then reloads. */
async function refresh(accountId: string): Promise<void> {
  const f = feedOf(accountId)
  set(f, { refreshing: true })
  try {
    await crmFetch('/v1/chats/online-spenders/refresh', { accountId, local: true, body: {} }).catch(() => undefined)
    await load(accountId)
  } finally {
    set(f, { refreshing: false })
  }
}

/** Ready to show: tracking is on and this server holds the Account's socket. */
export function spendersReady(state: SpendersState): state is SpendersPayload {
  return typeof state === 'object' && state.tracking_enabled === true && state.socket_held_here === true
}

/** Online now, biggest spenders first. */
export function onlineNow(state: SpendersState): Spender[] {
  return spendersReady(state) ? (state.spenders ?? []).filter((s) => s.is_online === true).sort((a, b) => Number(b.total_spent) - Number(a.total_spent)) : []
}

export function useOnlineSpenders(accountId: string) {
  const sub = useCallback((fn: () => void) => subscribe(accountId, fn), [accountId])
  const snap = useSyncExternalStore(sub, () => feedOf(accountId).snap)
  return { ...snap, refresh: () => refresh(accountId) }
}
